/**
 * WelcomeBackModal
 * Shown when the player returns after significant offline time.
 * Displays what buddies earned while away and lets them collect rewards.
 */

import { OfflineReport } from '@/systems/offline';
import { formatCurrency } from '@/data';

interface WelcomeBackModalProps {
  report: OfflineReport;
  onCollect: () => void;
}

/**
 * Format offline duration into human-readable string
 * e.g. "2h 34m", "45m", "8h 0m"
 */
function formatDuration(ms: number): string {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

export function WelcomeBackModal({ report, onCollect }: WelcomeBackModalProps) {
  const hasRewards =
    report.fishToInventory > 0 || report.goldEarned > 0 || report.xpEarned > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl p-6 mx-4 max-w-sm w-full text-center">
        <h2 className="text-2xl font-bold text-white mb-2">
          Welcome Back!
        </h2>

        <p className="text-gray-400 mb-4">
          You were away for {formatDuration(report.offlineDurationMs)}
        </p>

        {hasRewards ? (
          <>
            <p className="text-gray-300 mb-3">While you were gone:</p>
            <div className="space-y-2 mb-6 text-left">
              {report.fishToInventory > 0 && (
                <div className="flex items-center gap-2 text-gray-200">
                  <span className="text-lg">🐟</span>
                  <span>
                    Buddies caught{' '}
                    <span className="font-semibold text-blue-300">
                      {report.fishToInventory}
                    </span>{' '}
                    fish (added to inventory)
                  </span>
                </div>
              )}
              {report.fishSold > 0 && (
                <div className="flex items-center gap-2 text-gray-200">
                  <span className="text-lg">💰</span>
                  <span>
                    Haulers sold{' '}
                    <span className="font-semibold text-blue-300">
                      {report.fishSold}
                    </span>{' '}
                    fish for{' '}
                    <span className="font-semibold text-yellow-300">
                      {formatCurrency(report.goldEarned)}g
                    </span>
                  </span>
                </div>
              )}
              {report.goldEarned === 0 && report.fishToInventory > 0 && (
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <span className="text-lg">💡</span>
                  <span>Hire a hauler to auto-sell while you're away!</span>
                </div>
              )}
              {report.xpEarned > 0 && (
                <div className="flex items-center gap-2 text-gray-200">
                  <span className="text-lg">⭐</span>
                  <span>
                    You gained{' '}
                    <span className="font-semibold text-green-300">
                      {report.xpEarned}
                    </span>{' '}
                    XP
                  </span>
                </div>
              )}
            </div>
          </>
        ) : (
          <p className="text-gray-400 mb-6">
            Your buddies missed you! Hire buddies to earn while you're away.
          </p>
        )}

        <button
          onClick={onCollect}
          className="w-full py-3 px-6 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-500 active:bg-blue-700 transition-colors"
        >
          {hasRewards ? 'Collect!' : 'OK'}
        </button>
      </div>
    </div>
  );
}
